import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, Check } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const primaryCat = r.data.categories?.[0];
      if (primaryCat) {
        api
          .get("/products", { params: { category: primaryCat } })
          .then((res) => setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 4)));
      }
    });
  }, [slug]);

  if (!product) {
    return (
      <div className="px-6 py-32 text-center text-muted-foreground" data-testid="product-loading">
        Loading…
      </div>
    );
  }

  const handleAdd = () => {
    add(product, qty);
    toast.success(`Added ${qty} × ${product.name} to cart`);
  };

  return (
    <div data-testid={`product-page-${slug}`} className="animate-in fade-in duration-500">
      {/* Breadcrumb */}
      <div className="px-6 md:px-12 lg:px-24 pt-10 text-xs uppercase tracking-[0.18em] text-muted-foreground">
        <Link to="/" className="hover:text-secondary">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/category/${product.categories[0]}`} className="hover:text-secondary">
          {product.categories[0].replace("-", " ")}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-secondary">{product.name}</span>
      </div>

      <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
        {/* GALLERY */}
        <div className="md:col-span-7">
          <div className="bg-muted aspect-square overflow-hidden">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* INFO */}
        <div className="md:col-span-5 md:sticky md:top-32 self-start">
          <p className="overline text-primary mb-4" data-testid="product-category-label">
            {product.categories.map((c) => c.replace("-", " ")).join(" · ")}
          </p>
          <h1 className="editorial-heading text-4xl md:text-5xl text-secondary leading-[1.05] mb-4">
            {product.name}
          </h1>
          {(() => {
            const stock = Number(product.stock ?? 0);
            if (stock === 0 && product.pre_order) {
              return (
                <span
                  data-testid="product-preorder-badge"
                  className="inline-block bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.22em] font-semibold px-2.5 py-1 mb-4"
                >
                  Pre-order
                </span>
              );
            }
            if (stock === 0) {
              return (
                <span
                  data-testid="product-outofstock-badge"
                  className="inline-block bg-secondary text-secondary-foreground text-[10px] uppercase tracking-[0.22em] font-semibold px-2.5 py-1 mb-4"
                >
                  Out of stock
                </span>
              );
            }
            return null;
          })()}
          <p className="text-2xl text-secondary font-light mb-6" data-testid="product-price">
            ${product.price.toFixed(2)}
          </p>
          <p className="text-base text-muted-foreground leading-relaxed mb-6">
            {product.description}
          </p>

          <ul
            data-testid="product-includes-list"
            className="grid grid-cols-1 gap-2 mb-8 border-y border-border py-4"
          >
            {[
              "Includes bacteriostatic water for reconstitution",
              "Third-party tested for purity (≥ 98%)",
              "Certificate of Analysis available on request",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-2.5 text-xs text-secondary"
              >
                <Check className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center border border-border">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="product-qty-minus"
                className="p-3 hover:bg-muted"
                aria-label="Decrease quantity"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-5 text-sm font-medium" data-testid="product-qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="product-qty-plus"
                className="p-3 hover:bg-muted"
                aria-label="Increase quantity"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button
              onClick={handleAdd}
              disabled={Number(product.stock ?? 0) === 0 && !product.pre_order}
              data-testid="product-add-to-cart"
              className="flex-1 rounded-none bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground uppercase tracking-[0.18em] text-xs py-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-secondary disabled:hover:text-secondary-foreground"
            >
              {Number(product.stock ?? 0) === 0
                ? product.pre_order
                  ? "Pre-order"
                  : "Out of stock"
                : "Add to cart"}
            </Button>
          </div>

          {Number(product.stock ?? 0) === 0 && product.pre_order && (
            <p className="text-xs text-primary mb-6 leading-relaxed" data-testid="preorder-notice">
              This product is currently out of stock. Pre-order today and we&rsquo;ll dispatch as
              soon as it arrives back in stock — typically 2–4 weeks. You&rsquo;ll be charged at
              the time of ordering.
            </p>
          )}

          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-8">
            Delivery &amp; pickup — Melbourne only (Australia-wide coming soon) · Bac water included
          </p>

          <Accordion type="single" collapsible className="border-t border-border mt-2">
            <AccordionItem value="details" className="border-b border-border">
              <AccordionTrigger className="text-xs uppercase tracking-[0.2em] py-5">
                Peptide details
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-2 pb-6">
                {product.peptide_details?.sequence && (
                  <p><span className="text-secondary">Sequence:</span> {product.peptide_details.sequence}</p>
                )}
                {product.peptide_details?.molecular_mass && (
                  <p><span className="text-secondary">Molecular mass:</span> {product.peptide_details.molecular_mass}</p>
                )}
                {product.peptide_details?.purity && (
                  <p><span className="text-secondary">Purity:</span> {product.peptide_details.purity}</p>
                )}
                {product.peptide_details?.cas_number && (
                  <p><span className="text-secondary">CAS Number:</span> {product.peptide_details.cas_number}</p>
                )}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="dosing" className="border-b border-border">
              <AccordionTrigger className="text-xs uppercase tracking-[0.2em] py-5">
                Research notes
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-6">
                {product.dosing_notes}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping" className="border-b border-border">
              <AccordionTrigger className="text-xs uppercase tracking-[0.2em] py-5">
                Shipping & storage
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-6">
                Lyophilized peptides are stable at room temperature for short transit
                periods. For long-term storage: -20°C or below, protected from light.
                Shipped within 1 business day of order confirmation, directly from Melbourne.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-8 p-4 bg-muted border-l-2 border-primary">
            <p className="text-[11px] uppercase tracking-[0.22em] text-secondary mb-1 font-semibold">
              Research use only
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              This product is intended exclusively for laboratory research. It is not for human
              or veterinary consumption, diagnosis, or therapeutic use.
            </p>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24 border-t border-border">
          <h2 className="editorial-heading text-3xl md:text-4xl text-secondary mb-10">
            You may also study
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
